/* A textarea over a jsonb value.
 *
 * This is the escape hatch the curated pages are measured against: anything a
 * page cannot express, this can, because `tt_settings.value` is jsonb and the
 * console reads whatever is in it. It is also the only control in the app that
 * can write a shape the console will not understand, which is why it does the
 * two things a raw editor has to do — refuse to save text that is not JSON, and
 * never quietly replace what is on screen while it is being edited.
 *
 * That second one is the subtle half. The value arrives as a prop and is
 * re-serialised whenever it changes, which would wipe a half-typed edit the
 * moment a reload landed. So the box tracks whether it has been touched and
 * stops following the prop until it is saved or reverted.
 */
import * as Vue from 'vue';

function serialise(value) {
  if (value === undefined) return 'null';
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
}

export const JsonEditor = {
  name: 'JsonEditor',
  props: {
    value: { default: null },
    rows: { type: Number, default: 10 },
    // Shown on the save button. A key's name reads better than "Save" when
    // there are nine of these down one page.
    saveLabel: { type: String, default: 'Save' },
    busy: { type: Boolean, default: false }
  },
  emits: ['save'],
  setup: function (props, ctx) {
    var text = Vue.ref(serialise(props.value));
    var dirty = Vue.ref(false);
    var error = Vue.ref(null);

    Vue.watch(function () { return props.value; }, function (next) {
      if (dirty.value) return;      // mid-edit — see the header
      text.value = serialise(next);
      error.value = null;
    });

    function onInput(v) {
      text.value = v;
      dirty.value = true;
      error.value = null;
    }

    function revert() {
      text.value = serialise(props.value);
      dirty.value = false;
      error.value = null;
    }

    /* An empty box is `null`, not a parse error.
     *
     * Clearing the field is the obvious way to say "this salon has no theme",
     * and the console reads a null value as exactly that. Making somebody type
     * the four letters would be pedantry with a footgun in it — most people
     * would type nothing, get an error, and reach for the delete-the-key button
     * instead, which is a different operation.
     */
    function save() {
      var raw = text.value.trim();
      var parsed;
      if (raw === '') {
        parsed = null;
      } else {
        try {
          parsed = JSON.parse(raw);
        } catch (e) {
          error.value = String(e.message || e);
          return;
        }
      }
      dirty.value = false;
      ctx.emit('save', parsed);
    }

    return {
      text: text, dirty: dirty, error: error,
      onInput: onInput, revert: revert, save: save
    };
  },
  template: [
    '<div class="jsonbox" :class="{ \'is-dirty\': dirty, \'has-error\': error }">',
    '  <textarea class="jsonbox__area" spellcheck="false" :rows="rows"',
    '    :value="text" @input="onInput($event.target.value)"></textarea>',
    '  <p v-if="error" class="warn-line">Not valid JSON — {{ error }}</p>',
    '  <div class="jsonbox__row">',
    '    <button type="button" class="btn btn--primary btn--sm"',
    '      :disabled="busy || !dirty" @click="save">{{ saveLabel }}</button>',
    '    <button type="button" class="btn btn--quiet btn--sm"',
    '      :disabled="!dirty" @click="revert">Revert</button>',
    '    <span v-if="dirty" class="jsonbox__note">unsaved</span>',
    '  </div>',
    '</div>'
  ].join('\n')
};
